import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getBundleValueUp } from 'lib/bundleAndSave/getBundleValueUp'
import { isBundleOffer } from 'lib/offer/offerTypes'
import { addFlightPrice } from 'lib/offer/pricing'

export interface PriceOptions {
  flightPrice?: number;
  numberOfNights?: number;
}

function packagePriceDefault(
  pkg: App.Package | App.BundlePackageWithPrice,
  offer: App.Offer | App.OfferSummary | App.BundleOffer | App.BundleOfferSummary,
): {
  default: App.PricingWithValue
  member: App.PricingWithValue | undefined
} {
  const propertyFees = pkg.propertyFees

  const price = pkg.price + propertyFees
  let value = pkg.value + propertyFees
  let discountPercent = getValueOffPercent(value, price)
  const memberPrice = pkg.memberPrice > 0 ? pkg.memberPrice + propertyFees : undefined
  const memberValue = pkg.memberValue > 0 ? pkg.memberValue + propertyFees : undefined
  const saleUnit = offer.saleUnit
  let memberPricing: App.PricingWithValue | undefined

  const fees: Array<App.PricingFee> = [{
    type: 'property',
    amount: pkg.propertyFees,
  }]

  if (memberPrice || memberValue) {
    memberPricing = {
      // Using || rather than ?? because memberPrice and memberValue can be 0, which should be considered as undefined
      price: memberPrice || price,
      value: memberValue || value,
      discountPercent: getValueOffPercent(memberValue || value, memberPrice || price),
      fees,
      saleUnit,
    }
  }

  if (isBundleOffer(offer) && 'packages' in pkg) {
    const bundleValue = getBundleValueUp(offer, pkg, false)
    discountPercent = bundleValue.hotelDiscountPercent ?? 0
    value = bundleValue.valuedUpTo
    if (memberPricing) {
      const memberBundleValue = getBundleValueUp(offer, pkg, true)
      memberPricing.discountPercent = memberBundleValue.hotelDiscountPercent ?? 0
      memberPricing.value = memberBundleValue.valuedUpTo
    }
  }

  return {
    default: {
      price,
      value,
      discountPercent,
      fees,
      saleUnit,
    },
    member: memberPricing,
  }
}

function ratePriceDefault(offer: App.BedbankOffer | App.BedbankOfferSummary | undefined, rate?: App.BedbankRate): {
  default: App.PricingWithValue
  member: App.PricingWithValue | undefined
} | undefined {
  const propertyFees = rate?.totals?.propertyFees ?? 0
  const hotelPrice = rate ? rate.totals.inclusive : offer?.sell?.price
  const hotelMemberPrice = rate?.totals.memberInclusive ?? 0
  if (!hotelPrice) return
  const value = rate?.value ?? offer?.sell?.value ?? hotelPrice
  const price = hotelPrice + propertyFees
  let memberPricing: App.PricingWithValue | undefined

  const fees: Array<App.PricingFee> = [{
    type: 'property',
    amount: propertyFees,
  }]

  if (hotelMemberPrice) {
    const memberPrice = hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0
    memberPricing = {
      price: memberPrice,
      value,
      discountPercent: getValueOffPercent(value, memberPrice),
      fees,
      saleUnit: 'total',
    }
  }

  return {
    default: {
      price,
      value,
      discountPercent: getValueOffPercent(value, price),
      fees,
      saleUnit: 'total',
    },
    member: memberPricing,
  }
}

/**
 * Returns pricing for offer or package if supplied without taking into account occupancies/rooms
 */
export function useOfferPackagePrice(
  pkg: App.Package | App.BundlePackageWithPrice,
  offer: App.Offer | App.OfferSummary | App.BundleOffer | App.BundleOfferSummary,
  options?: PriceOptions,
): {
  default: App.PricingWithValue
  member?: App.PricingWithValue
  withFlights?: App.PricingWithValue
  memberWithFlights?: App.PricingWithValue
} {
  const basePriceDetails = packagePriceDefault(pkg, offer)

  return addFlightPriceVariants(basePriceDetails, options?.flightPrice)
}

/**
 * Returns pricing for bedbank offer or rate if supplied without taking into account occupancies/rooms
 */
export function useOfferRatePrice(
  offer: App.BedbankOffer | App.BedbankOfferSummary | undefined,
  rate?: App.BedbankRate,
  options?: PriceOptions,
): {
  default?: App.PricingWithValue
  member?: App.PricingWithValue
  withFlights?: App.PricingWithValue
  memberWithFlights?: App.PricingWithValue
} {
  const basePriceDetails = ratePriceDefault(offer, rate)
  if (!basePriceDetails) return {}
  return addFlightPriceVariants({
    default: basePriceDetails.default,
    member: basePriceDetails.member,
  }, options?.flightPrice)
}

function addFlightPriceVariants(pricing: {
  default: App.PricingWithValue
  member?: App.PricingWithValue
}, flightPrice?: number): {
  default: App.PricingWithValue
  member?: App.PricingWithValue
  withFlights?: App.PricingWithValue
  memberWithFlights?: App.PricingWithValue
} {
  if (!flightPrice) {
    return pricing
  }
  return {
    ...pricing,
    withFlights: addFlightPrice(pricing.default, flightPrice),
    memberWithFlights: pricing.member ? addFlightPrice(pricing.member, flightPrice) : undefined,
  }
}
